/**
 * Custom Action Resolver
 *
 * Provides a centralized system for registering custom actions for fields.
 * Custom actions can be used to override default field behaviors like edit handlers.
 *
 * Usage:
 *
 * // Register a custom action
 * addCustomAction("StoreProduct.price.openPriceEditor", async (product) => {
 *   // Custom logic
 *   return true // or false
 * })
 *
 * // Get an action
 * const action = getCustomAction("StoreProduct.price.openPriceEditor")
 * if (action) await action(product)
 */

const customActions = {}

/**
 * Register a custom action
 * @param {string} actionKey - Action key in format "TypeName.fieldId.actionName"
 * @param {Function} handler - Action handler function
 */
export const addCustomAction = (actionKey, handler) => {
  if (!actionKey || typeof actionKey !== 'string') {
    throw new Error('Action key must be a non-empty string')
  }

  if (typeof handler !== 'function') {
    throw new Error('Handler must be a function')
  }

  customActions[actionKey] = handler
}

/**
 * Get a custom action handler
 * @param {string} actionKey - Action key in format "TypeName.fieldId.actionName"
 * @returns {Function|null} Action handler or null if not found
 */
export const getCustomAction = (actionKey) => {
  return customActions[actionKey] || null
}

/**
 * Check if an action exists
 * @param {string} actionKey - Action key in format "TypeName.fieldId.actionName"
 * @returns {boolean}
 */
export const hasCustomAction = (actionKey) => {
  return !!customActions[actionKey]
}

/**
 * Clear all custom actions (useful for testing)
 */
export const clearCustomActions = () => {
  Object.keys(customActions).forEach(key => delete customActions[key])
}
